import * as babylon from "@babylonjs/core";

import type { BabylonManager } from "@/shared/components/visualization/Panel/ThreeDPanel/BabylonManager";
import { measureModel } from "@/shared/components/visualization/Panel/ThreeDPanel/measureModel";
import { framingCamera } from "@/shared/components/visualization/Panel/ThreeDPanel/modelFraming";
import {
  readJointsFromArticulation,
  type ModelAssetLoader,
} from "@/shared/components/visualization/Panel/ThreeDPanel/ModelLoader";
import { PanelType, type ThreeDPanelState } from "@/shared/state/visualization";
import type { File } from "@/shared/state/visualization/schema/v1/file";
import type {
  Grid,
  Joint,
  ThirdPersonCamera,
} from "@/shared/state/visualization/schema/v1/three-d";

import { restingJointValue } from "./jointPosing";
import { previewGrid } from "./previewFraming";

/**
 * A model file, turned into something `BabylonManager` can render and a list of
 * joints a sidebar can pose.
 *
 * This type is the **only** thing the preview UI knows about how a glTF becomes
 * a scene. Everything below — that the file is drawn by a single `model`
 * component, that a joint is driven by writing a value against its name — is
 * confined to this module, so a future change in how a model is represented is a
 * change to this file and nothing else.
 */
export interface PreviewScene {
  /**
   * The panel state to hand `BabylonManager`, framed by
   * {@link PreviewScene.defaults}.
   *
   * One entity drawing the whole file, and constant for as long as the model is
   * loaded: posing a joint writes renderer component state, never this. See
   * {@link applyJointPose}. Scenery and viewpoint *are* panel state, so an edit
   * to either produces a fresh state through {@link sceneStateWith} rather than
   * being written here.
   */
  state: ThreeDPanelState;
  /** The model's joints, in the order the file declares them. */
  joints: Joint[];
  /**
   * The scenery and viewpoint the model's own bounding box implies.
   *
   * Carried out separately from `state` — where they are already applied —
   * because they are the values an editor starts from and the values a reset
   * would return to, and because `state.config.camera` is typed as the whole
   * camera union while everything here knows it is third person.
   *
   * Derived **once**, when the model is measured. Re-deriving them as the user
   * edits would overwrite every change with the computed value again.
   */
  defaults: PreviewSceneConfig;
}

/**
 * The parts of a preview's scene the user can adjust: what the floor looks like,
 * and where the camera stands.
 *
 * Everything else about the scene is the model, and the model is not editable
 * here — this wizard step is a check on a conversion, not a scene editor.
 */
export interface PreviewSceneConfig {
  camera: ThirdPersonCamera;
  grid: Grid;
}

/**
 * The preview's panel state with `config` re-framed, leaving the model alone.
 *
 * The entity, its `model` component and the joints it declares are shared with
 * the state the scene was built with, so handing the result to the renderer
 * updates the camera and the grid **without** restaling the model: the manager
 * compares the mesh spec it holds against the one it is given, and an identical
 * spec means the loaded copy stays loaded. Rebuilding the whole state object
 * from scratch on every keystroke is what that would have cost.
 */
export function sceneStateWith(
  scene: PreviewScene,
  config: PreviewSceneConfig,
): ThreeDPanelState {
  return {
    ...scene.state,
    config: { ...scene.state.config, camera: config.camera, grid: config.grid },
  };
}

/** Every preview draws the same single-panel scene; the id is never displayed. */
const PREVIEW_PANEL_ID = "urdf-preview";

/**
 * The entity the model hangs off. Never displayed, and there is only ever one.
 *
 * Exported so a test can address the same entity the pose is written against
 * without restating the string.
 */
export const PREVIEW_ENTITY_ID = "model";

/**
 * Loads a model file into a renderable, posable scene.
 *
 * Reuses the 3D panel's own model path wholesale: the file becomes **one**
 * `model` mesh component owning one instantiated copy of the document's node
 * hierarchy, and the joints the file declares move nodes inside that copy. The
 * panel instantiates it from the loader's template cache when it reconciles this
 * state, so nothing here builds geometry.
 *
 * The load below therefore exists for two things the panel state needs up front
 * and cannot get later: the declared articulation, and the model's size. The
 * parse happens in a throwaway `NullEngine` scene — reading either needs a
 * parsed glTF and therefore *a* scene, but what is wanted out of it is plain
 * data. The container is disposed immediately; the loader's template survives it,
 * so the canvas draws from the same parse rather than fetching again.
 *
 * The camera and the grid are **sized from the model's own bounding box**. The
 * panel's defaults are tuned for its usual subject — vehicle trajectories
 * hundreds of metres across — and a robot is one metre; see the panel's shared
 * `modelFraming.ts` for the camera and `previewFraming.ts` for the grid.
 * Neither object is built here: setting
 * `config.camera` and `config.grid` is enough, and the panel's scene config
 * produces both.
 *
 * @param loader the loader the canvas will also draw through
 * @param file the model file to load
 * @throws if the file cannot be loaded, or contains no nodes to draw
 */
export async function buildPreviewScene(
  loader: ModelAssetLoader,
  file: File,
): Promise<PreviewScene> {
  const engine = new babylon.NullEngine();
  const scene = new babylon.Scene(engine);
  try {
    const { container, articulation, orientation } = await loader.load(
      file.fileId,
      scene,
      new AbortController().signal,
    );
    const joints = readJointsFromArticulation(articulation);
    // Measured before the container goes: it owns the only geometry there is,
    // and the panel's copy of it does not exist yet.
    // Turned by the loader's correction, which the panel will apply above the
    // copy's roots: the camera frames the model as it is drawn.
    const bounds = measureModel(container, orientation);
    // Counted the way the renderer will index the copy — the descendants of the
    // roots, which for a glTF are everything below Babylon's synthetic
    // `__root__`. A document with none draws nothing at all, whatever its
    // articulation claims.
    const drawableNodes = container.rootNodes.reduce(
      (count, root) => count + root.getDescendants(false).length,
      0,
    );
    container.dispose();

    if (drawableNodes === 0) {
      throw new Error("This model has no nodes to display");
    }

    const defaults: PreviewSceneConfig = {
      camera: framingCamera(bounds),
      // The bounds frame the camera only: the grid is the panel's standard one,
      // not a spacing derived from this model.
      grid: previewGrid(),
    };

    return {
      joints,
      defaults,
      state: {
        type: PanelType.ThreeD,
        id: PREVIEW_PANEL_ID,
        config: {
          entities: { [PREVIEW_ENTITY_ID]: {} },
          camera: defaults.camera,
          grid: defaults.grid,
        },
        data: {
          entities: {
            [PREVIEW_ENTITY_ID]: {
              id: PREVIEW_ENTITY_ID,
              // `joints` is carried into the spec because that is what the
              // renderer poses against: it reads each joint's type, axis, limits
              // and `mimic` from here rather than from the file, so the sidebar
              // and the renderer cannot disagree about what a joint is.
              mesh: { type: "model", file, joints },
            },
          },
        },
      },
    };
  } finally {
    scene.dispose();
    engine.dispose();
  }
}

/**
 * The pose a model is being held in: the value each joint sits at, by joint
 * name — radians for a joint that turns, metres for one that slides. A joint
 * absent from the record is left wherever its spec puts it.
 *
 * Keyed by name because that is what the sidebar has, what the model file
 * declares, and now also what the renderer is addressed by — the three agree
 * with no translation step in between.
 */
export type JointPose = Readonly<Record<string, number>>;

/**
 * The pose a model is in before anyone touches it: every joint at the value in
 * its range closest to rest, per {@link restingJointValue}.
 *
 * Not the empty record. An empty pose reads as "every joint at zero", which is a
 * pose a joint whose limits exclude zero cannot physically be in — and it is the
 * pose the sliders would have claimed the robot was in.
 *
 * Joints with no value of their own — fixed, floating, planar, coupled by
 * `mimic`, or with an inverted range — are absent rather than zero, which is
 * what keeps them at the transform the file gave them and out of
 * {@link applyJointPose}'s reach.
 */
export function restingPose(joints: Joint[]): JointPose {
  const pose: Record<string, number> = {};
  for (const joint of joints) {
    const value = restingJointValue(joint);
    if (value !== undefined) {
      pose[joint.name] = value;
    }
  }
  return pose;
}

/**
 * The slice of `BabylonManager` a pose is written through.
 *
 * Narrow on purpose: posing is three imperative calls and nothing else, and a
 * structural type lets the seam be exercised without a WebGL context.
 */
export type JointPoseTarget = Pick<
  BabylonManager,
  "setModelJointPose" | "clearModelJointPose" | "getModelJointPose"
>;

/**
 * Puts the live scene into `pose`, by writing ECS component state directly.
 *
 * A joint value is **not** a change to the scene's state. The panel state holds
 * the model as loaded — one `model` component naming a file and the joints it
 * declares — and it stays exactly that however far the user drives a joint. What
 * moves is the renderer's own live joint value, which `setModelJointPose` writes
 * and the render loop applies each frame.
 *
 * Posing through `state` instead is what this replaces, and it was visibly
 * broken: a changed spec makes the manager restale the joint's binding for its
 * next reload poll, so the joint and its whole subtree flashed back to the rest
 * pose on every drag tick.
 *
 * **Only a value is handed over.** Which axis a joint moves about, whether it
 * turns or slides, and what it is coupled to are all read by the renderer from
 * the model's own declaration, which reached it through `mesh.joints`. That is
 * what keeps the classic "rotate the axis into Y-up first" mistake — and its
 * subtler cousin, mapping the axis into whatever frame the renderer happens to
 * hold transforms in — out of the preview entirely: there is no axis here to get
 * wrong.
 *
 * Reconciles rather than blindly writes: a joint absent from `pose` has its pose
 * cleared, and a joint already at its value is left alone, so dragging one
 * slider does not touch the other joints.
 *
 * A joint with no value of its own — fixed, floating or planar, one coupled to
 * another by `mimic`, one whose limits are inverted, or one the file declares no
 * axis for — is never posed, whatever the caller passes for it, and keeps the
 * transform the file gave it. {@link restingJointValue} is the single test for
 * that, which is why a **pinned** joint is not on the list: it has no slider, but
 * it does have a value, and it is held at that value rather than left wherever
 * the file's zero pose put it.
 */
export function applyJointPose(
  scene: PreviewScene,
  manager: JointPoseTarget,
  pose: JointPose,
): void {
  for (const joint of scene.joints) {
    const value = pose[joint.name];
    if (value === undefined || restingJointValue(joint) === undefined) {
      manager.clearModelJointPose(PREVIEW_ENTITY_ID, joint.name);
      continue;
    }
    if (manager.getModelJointPose(PREVIEW_ENTITY_ID, joint.name) === value) {
      continue;
    }
    manager.setModelJointPose(PREVIEW_ENTITY_ID, joint.name, value);
  }
}
